// 最適化された画像アップロード処理

/**
 * クライアントサイド画像最適化
 * アップロード前にブラウザで画像を圧縮・リサイズ
 */
class OptimizedImageUploader {
  constructor(options = {}) {
    this.maxWidth = options.maxWidth || 1920;
    this.maxHeight = options.maxHeight || 1080;
    this.quality = options.quality || 0.8;
    this.enableClientResize = options.enableClientResize !== false;
  }

  /**
   * ファイルを最適化してからアップロード
   */
  async uploadFiles(files, options = {}) {
    const optimizedFiles = [];
    
    // 並行処理でファイルを最適化
    const optimizationPromises = Array.from(files).map(async (file, index) => {
      if (!file.type.startsWith('image/')) {
        return { file, index };
      }

      try {
        // クライアントサイドで画像を最適化
        const optimizedFile = this.enableClientResize 
          ? await this.optimizeImage(file)
          : file;
          
        return { file: optimizedFile, index };
      } catch (error) {
        console.warn('画像最適化に失敗:', error);
        return { file, index };
      }
    });

    const results = await Promise.all(optimizationPromises);
    
    // 元の順序を保持
    results.sort((a, b) => a.index - b.index);
    results.forEach(result => optimizedFiles.push(result.file));

    // プログレス付きアップロード
    return this.uploadWithProgress(optimizedFiles, options);
  }

  /**
   * Canvas APIを使用した画像最適化
   */
  async optimizeImage(file) {
    return new Promise((resolve) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      img.onload = () => {
        // アスペクト比を保持してリサイズ
        const { width, height } = this.calculateNewDimensions(
          img.width, 
          img.height
        );

        canvas.width = width;
        canvas.height = height;

        // 高品質でリサイズ
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);

        // Blobに変換
        canvas.toBlob(
          (blob) => {
            // ファイルサイズが削減された場合のみ採用
            if (blob.size < file.size) {
              const optimizedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now()
              });
              resolve(optimizedFile);
            } else {
              resolve(file);
            }
          },
          file.type,
          this.quality
        );
      };

      img.onerror = () => resolve(file);
      img.src = URL.createObjectURL(file);
    });
  }

  /**
   * 新しい画像サイズを計算
   */
  calculateNewDimensions(originalWidth, originalHeight) {
    let { width, height } = { width: originalWidth, height: originalHeight };

    if (width > this.maxWidth) {
      height = (height * this.maxWidth) / width;
      width = this.maxWidth;
    }

    if (height > this.maxHeight) {
      width = (width * this.maxHeight) / height;
      height = this.maxHeight;
    }

    return { width: Math.round(width), height: Math.round(height) };
  }

  /**
   * プログレス付きアップロード
   */
  async uploadWithProgress(files, options = {}) {
    const formData = new FormData();
    
    // ファイルを追加
    files.forEach((file, index) => {
      formData.append(`pictures[${index}]`, file);
    });

    // その他のパラメータを追加
    Object.entries(options).forEach(([key, value]) => {
      formData.append(key, value);
    });

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // プログレス監視
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          this.onProgress?.(percentComplete);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch (error) {
            reject(new Error('レスポンスの解析に失敗しました'));
          }
        } else {
          reject(new Error(`アップロードに失敗しました: ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('ネットワークエラーが発生しました'));
      });

      // 最適化されたアップローダーを使用
      xhr.open('POST', '/img_converter/optimized_uploader.php');
      xhr.send(formData);
    });
  }

  /**
   * プログレスコールバックを設定
   */
  onProgress(callback) {
    this.onProgress = callback;
    return this;
  }
}

/**
 * WebP形式対応の高度な最適化
 */
class AdvancedImageUploader extends OptimizedImageUploader {
  constructor(options = {}) {
    super(options);
    this.useWebP = options.useWebP && this.supportsWebP();
  }

  /**
   * WebPサポートを確認
   */
  supportsWebP() {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  }

  /**
   * WebPを使用した最適化
   */
  async optimizeImage(file) {
    if (!this.useWebP || !file.type.startsWith('image/')) {
      return super.optimizeImage(file);
    }

    return new Promise((resolve) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      img.onload = () => {
        const { width, height } = this.calculateNewDimensions(
          img.width, 
          img.height
        );

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        // WebPで圧縮
        canvas.toBlob(
          (blob) => {
            const webpFile = new File([blob], file.name.replace(/\.[^/.]+$/, '.webp'), {
              type: 'image/webp',
              lastModified: Date.now()
            });
            resolve(webpFile);
          },
          'image/webp',
          this.quality
        );
      };

      img.onerror = () => resolve(file);
      img.src = URL.createObjectURL(file);
    });
  }
}

// 使用例
const uploader = new OptimizedImageUploader({
  maxWidth: 1920,
  maxHeight: 1080,
  quality: 0.8
});

uploader.onProgress = (percent) => {
  console.log(`アップロード進行度: ${percent}%`);
  // UIのプログレスバーを更新
  updateProgressBar(percent);
};

// MapLife用のグローバル関数として登録
window.OptimizedImageUploader = OptimizedImageUploader;
window.AdvancedImageUploader = AdvancedImageUploader;

export { OptimizedImageUploader, AdvancedImageUploader };